// Centralized Notes Service
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Generate unique ID for notes
 */
function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `note_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}

/**
 * Notes Service - Centralized note management
 *
 * Note structure:
 * {
 *   id: string,
 *   prospectId: string | null,
 *   trackerId: string | null,
 *   emailId: string | null,
 *   text: string,
 *   createdAt: number (milliseconds timestamp),
 *   createdAtISO: string,
 *   createdBy: string,
 *   tracker: string ('prospect' | '4x4' | 'associate' | 'production' | 'licensing' | null),
 *   updatedAt: Timestamp (Firestore server timestamp)
 * }
 */

/**
 * Add a note
 * @param {Object} noteData
 */
/**
 * Add a note
 * @param {Object} noteData - Note data
 * @param {string} noteData.prospectId - Prospect ID (optional)
 * @param {string} noteData.trackerId - Tracker ID (optional)
 * @param {string} noteData.emailId - Email identifier to group all notes for a person
 * @param {string} noteData.text - Note text
 * @param {string} noteData.createdBy - Author name
 * @param {string} noteData.tracker - Tracker type ('prospect', '4x4', 'associate', etc.)
 * @returns {Promise<string>} Note ID
 */
export async function addNote({ prospectId = null, trackerId = null, emailId = null, text, createdBy, tracker = null }) {
  const noteId = uniqueId();
  const now = new Date();
  const nowTimestamp = now.getTime();
  const nowIso = now.toISOString();

  const noteData = {
    id: noteId,
    prospectId,
    trackerId,
    emailId, // Email to group all notes for a person
    text: text?.trim() ?? "",
    createdAt: nowTimestamp,
    createdAtISO: nowIso,
    createdBy: createdBy || "Unknown",
    tracker,
    updatedAt: serverTimestamp(),
  };

  console.log(`[notesService] Saving note to Firestore:`, noteData);
  const noteRef = doc(db, "notes", noteId);
  await setDoc(noteRef, noteData);
  console.log(`[notesService] Note saved with ID: ${noteId}`);

  return noteId;
}

/**
 * Get notes for a specific prospect
 * @param {string} prospectId
 * @returns {Promise<Array>}
 */
export async function getProspectNotes(prospectId) {
  const q = query(collection(db, "notes"), where("prospectId", "==", prospectId));
  const snapshot = await getDocs(q);
  const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  notes.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  return notes;
}

/**
 * Get notes for a specific tracker
 * @param {string} trackerId
 * @param {string|null} trackerType
 * @returns {Promise<Array>}
 */
export async function getTrackerNotes(trackerId, trackerType = null) {
  let q;
  if (trackerType) {
    q = query(
      collection(db, "notes"),
      where("trackerId", "==", trackerId),
      where("tracker", "==", trackerType)
    );
  } else {
    q = query(collection(db, "notes"), where("trackerId", "==", trackerId));
  }

  const snapshot = await getDocs(q);
  const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  notes.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  return notes;
}

/**
 * Subscribe to prospect notes (real-time)
 * @param {string} prospectId
 * @param {Function} callback
 * @returns {Function} unsubscribe
 */
export function subscribeToProspectNotes(prospectId, callback) {
  console.log(`[notesService] Subscribing to notes for prospect: ${prospectId}`);

  try {
    const q = query(collection(db, "notes"), where("prospectId", "==", prospectId));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const notes = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log(`[notesService] Note document:`, { id: doc.id, ...data });
          return { id: doc.id, ...data };
        });
        notes.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        console.log(`[notesService] Snapshot received for prospect ${prospectId}: ${notes.length} notes`);
        callback(notes);
      },
      error => {
        console.error(`[notesService] Error subscribing to notes for prospect ${prospectId}:`, error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error(`[notesService] Error setting up subscription for prospect ${prospectId}:`, error);
    callback([]);
    return () => {};
  }
}

/**
 * Subscribe to tracker notes (real-time)
 * @param {string} trackerId
 * @param {string|null} trackerType
 * @param {Function} callback
 * @returns {Function} unsubscribe
 */
export function subscribeToTrackerNotes(trackerId, trackerType = null, callback) {
  try {
    let q;
    if (trackerType) {
      q = query(
        collection(db, "notes"),
        where("trackerId", "==", trackerId),
        where("tracker", "==", trackerType)
      );
    } else {
      q = query(collection(db, "notes"), where("trackerId", "==", trackerId));
    }

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        notes.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        callback(notes);
      },
      error => {
        console.error(`[notesService] Error subscribing to tracker ${trackerId}:`, error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error(`[notesService] Error setting up subscription for tracker ${trackerId}:`, error);
    callback([]);
    return () => {};
  }
}

/**
 * Delete a note
 * @param {string} noteId
 */
export async function deleteNote(noteId) {
  const noteRef = doc(db, "notes", noteId);
  await deleteDoc(noteRef);
}

/**
 * Update a note's text
 * @param {string} noteId
 * @param {string} text
 */
export async function updateNote(noteId, text) {
  const noteRef = doc(db, "notes", noteId);
  await setDoc(
    noteRef,
    {
      text: text?.trim() ?? "",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Get all notes by tracker type
 * @param {string} trackerType
 * @returns {Promise<Array>}
 */
export async function getNotesByTrackerType(trackerType) {
  const q = query(collection(db, "notes"), where("tracker", "==", trackerType), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Get notes by author
 * @param {string} authorName
 * @returns {Promise<Array>}
 */
export async function getNotesByAuthor(authorName) {
  const q = query(collection(db, "notes"), where("createdBy", "==", authorName), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to notes for a prospect by email (cross-reference)
 * Useful for linking prospect notes to tracker records
 * @param {string} prospectEmail - Email of the prospect
 * @param {Function} callback - Callback function to receive notes
 * @returns {Function} Unsubscribe function
 */
export function subscribeToProspectNotesByEmail(prospectEmail, callback) {
  if (!prospectEmail) {
    callback([]);
    return () => {};
  }
  
  console.log(`[notesService] Subscribing to notes for prospect email: ${prospectEmail}`);
  
  try {
    // First, find the prospect by email
    const prospectsRef = collection(db, "prospects");
    const prospectQuery = query(prospectsRef, where("email", "==", prospectEmail));
    
    // Subscribe to prospects collection to find matching prospect
    const unsubProspects = onSnapshot(
      prospectQuery,
      (prospectSnap) => {
        if (prospectSnap.empty) {
          console.log(`[notesService] No prospect found with email: ${prospectEmail}`);
          callback([]);
          return;
        }
        
        const prospectId = prospectSnap.docs[0].id;
        
        // Now subscribe to notes for this prospect
        const notesQuery = query(
          collection(db, "notes"),
          where("prospectId", "==", prospectId)
        );
        
        const unsubNotes = onSnapshot(
          notesQuery,
          (notesSnap) => {
            const notes = notesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log(`[notesService] Received ${notes.length} prospect notes for email ${prospectEmail}`);
            callback(notes);
          },
          (error) => {
            console.error(`[notesService] Error subscribing to prospect notes for email ${prospectEmail}:`, error);
            callback([]);
          }
        );
        
        // Return the notes unsubscribe (prospects won't change often)
        return unsubNotes;
      },
      (error) => {
        console.error(`[notesService] Error finding prospect by email ${prospectEmail}:`, error);
        callback([]);
      }
    );
    
    return unsubProspects;
  } catch (error) {
    console.error(`[notesService] Error setting up subscription for prospect email ${prospectEmail}:`, error);
    callback([]);
    return () => {};
  }
}

/**
 * Subscribe to notes by email ID
 * This fetches all notes for a person across all trackers in one subscription
 * @param {string} emailId - Email identifier
 * @param {Function} callback - Callback function to receive notes
 * @returns {Function} Unsubscribe function
 */
export function subscribeToNotesByEmailId(emailId, callback) {
  if (!emailId) {
    console.log(`[notesService] subscribeToNotesByEmailId: emailId is empty`);
    callback([]);
    return () => {};
  }

  console.log(`[notesService] Subscribing to notes for emailId: ${emailId}`);

  try {
    const q = query(
      collection(db, "notes"),
      where("emailId", "==", emailId)
    );

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const notes = snapshot.docs.map(doc => {
          const data = doc.data();
          return { id: doc.id, ...data };
        });
        notes.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        console.log(`[notesService] Received ${notes.length} notes for emailId ${emailId}`);
        callback(notes);
      },
      error => {
        console.error(`[notesService] Error subscribing to notes for emailId ${emailId}:`, error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error(`[notesService] Error setting up subscription for emailId ${emailId}:`, error);
    callback([]);
    return () => {};
  }
}

/**
 * Get notes by email ID - one-time fetch
 * @param {string} emailId - Email identifier
 * @returns {Promise<Array>}
 */
export async function getNotesByEmailId(emailId) {
  if (!emailId) return [];

  const q = query(
    collection(db, "notes"),
    where("emailId", "==", emailId)
  );

  const snapshot = await getDocs(q);
  const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  notes.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  return notes;
}

/**
 * Test/Debug function - get all notes from collection
 * @returns {Promise<Array>}
 */
export async function getAllNotes() {
  try {
    const snapshot = await getDocs(collection(db, "notes"));
    const allNotes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    console.log(`[notesService] Total notes in collection: ${allNotes.length}`, allNotes);
    return allNotes;
  } catch (error) {
    console.error(`[notesService] Error getting all notes:`, error);
    return [];
  }
}

/**
 * Test/Debug function - subscribe to ALL notes
 * @param {Function} callback
 * @returns {Function} unsubscribe
 */
export function subscribeToAllNotes(callback) {
  console.log(`[notesService] Subscribing to ALL notes (debug)`);

  try {
    const q = query(collection(db, "notes"));
    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const notes = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log(`[notesService] Note doc ${doc.id}:`, data);
          return { id: doc.id, ...data };
        });
        console.log(`[notesService] ALL notes snapshot: ${notes.length} total notes`);
        callback(notes);
      },
      error => {
        console.error(`[notesService] Error in subscribeToAllNotes:`, error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error(`[notesService] Error setting up subscribeToAllNotes:`, error);
    callback([]);
    return () => {};
  }
}

// Default export for backward compatibility
export default {
  addNote,
  getProspectNotes,
  getTrackerNotes,
  subscribeToProspectNotes,
  subscribeToProspectNotesByEmail,
  subscribeToNotesByEmailId,
  subscribeToTrackerNotes,
  deleteNote,
  updateNote,
  getNotesByTrackerType,
  getNotesByAuthor,
  getNotesByEmailId,
  getAllNotes,
  subscribeToAllNotes,
};
